import classNames from "classnames";
import AppContext from "PFApp/app_context";
import { Button } from "PFComponents/button";
import NoBackdropDropdownButton from "PFComponents/dropdown/no_backdrop_dropdown_button";
import { useQueryParams } from "PFCore/helpers/use_query_params";
import { useSearchInput } from "PFCore/helpers/use_search_input";
import ArrowDownIcon from "PFIcons/arrow_down.svg";
import BubbleIcon from "PFIcons/bubble.svg";
import CloseIcon from "PFIcons/close.svg";
import EditIcon from "PFIcons/edit.svg";
import ListIcon from "PFIcons/list_ul.svg";
import SearchIcon from "PFIcons/search_fat.svg";
import TreeMapIcon from "PFIcons/tree_map.svg";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import SkillsExplorer from "./charts/skills_explorer";
import PageSection from "./page_section";
import css from "./skills_chart_section.modules.scss";
import { SkillsPowerList } from "./skills_power_list";

const SkillsChartSection = ({ profile, className }) => {
  const {
    store: { currentProfile }
  } = useContext(AppContext);
  const { t } = useTranslation("profiles");

  const chartOptions = [
    {
      id: "drilldown",
      displayElement: (
        <div className={css.dropdownIcon}>
          <TreeMapIcon height={20} width={20} />
        </div>
      ),
      item: () => setSelectedChart("drilldown")
    },
    {
      id: "bubble",
      displayElement: (
        <div className={css.dropdownIcon}>
          <BubbleIcon height={20} width={20} />
        </div>
      ),
      item: () => setSelectedChart("bubble")
    }
  ];

  const [selectedChart, setSelectedChart] = useState(
    currentProfile.preferences.display?.skills_insights || chartOptions[1].id
  );
  const [isListVisible, setIsListVisible] = useState(false);
  const [wasChartLasView, setWasChartLasView] = useState(true);

  const searchParams = useQueryParams();

  const [searchTerm, setSearchTerm] = useState(searchParams.get("term"));
  const searchInputRef = useRef(false);

  const { handleSearchKeyDown, handleClear, handleDebounceChange } = useSearchInput({
    searchInputRef,
    onTermChange: setSearchTerm
  });

  useEffect(() => {
    if (searchTerm?.length > 0) {
      isListVisible && setWasChartLasView(false);
      setIsListVisible(true);
    } else if (searchTerm === null) {
      setIsListVisible(wasChartLasView ? false : true);
    }
  }, [searchTerm]);

  const isMeProfile = currentProfile.id === profile.id;

  const chartIcon = chartOptions.find(({ id }) => id === selectedChart).displayElement;

  const skills = useMemo(
    () =>
      (profile.skills || []).filter((item) =>
        (item.text || item.value || "").toLowerCase().includes(searchTerm?.toLowerCase() || "")
      ),
    [searchTerm, profile.id, JSON.stringify(profile.skills)]
  );

  const skillsExplorerAvailable = profile.skill_categories && profile.skill_categories.length > 0;

  const actions = (
    <div className={css.actionsWrapper}>
      {isMeProfile && (
        <Link to={`/profiles/me/summary/skills`}>
          <Button icon="add" onClick={(fn) => fn} />
        </Link>
      )}
      <label className={css.box}>
        <div className={css.input}>
          <input
            ref={searchInputRef}
            onBlur={() => !searchInputRef?.current.value && setTimeout(() => setSearchTerm(""), 200)}
            onChange={handleDebounceChange}
            onKeyDown={handleSearchKeyDown}
            defaultValue={searchTerm || ""}
            placeholder={t("show.parts.search")}
          />
        </div>

        {searchTerm && searchTerm?.length > 0 ? (
          <button className={css.clear} onClick={handleClear} title={t("translation:clear")}>
            <CloseIcon width={20} height={20} />
          </button>
        ) : (
          <SearchIcon width={18} height={18} className={css.icon} />
        )}
      </label>
      <div className={css.separator} />
      <div className={css.buttons}>
        {skillsExplorerAvailable && (
          <>
            <NoBackdropDropdownButton
              buttonStyle={{ opacity: isListVisible ? 0.3 : 1, padding: "0 10px" }}
              dropDownStyle={{ minWidth: 70 }}
              dropDownClassName={css.dropdownItem}
              buttonKind="blank"
              size="sm"
              options={chartOptions}
              popperOptions={{
                placement: "bottom",
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [-2, 15]
                    }
                  }
                ]
              }}
              handleChange={(fn) => {
                fn && fn();
                setIsListVisible(false);
                setWasChartLasView(true);
                isListVisible && handleClear();
              }}
            >
              <div className={css.dropdownIcons}>
                {chartIcon}
                <ArrowDownIcon width={10} height={10} className={css.arrowIcon} />
              </div>
            </NoBackdropDropdownButton>
            <div className={css.separator} />
          </>
        )}
        <button
          className={classNames(css.buttonNoStyle, { [css.iconInnactive]: !isListVisible })}
          onClick={() =>
            setIsListVisible((prev) => {
              if (prev === true) {
                setSelectedChart((prev) => prev);
                return false;
              } else {
                return true;
              }
            })
          }
        >
          <ListIcon height={20} width={20} className={css.actionIcon} />
        </button>
        <div className={css.separator} />
        {isMeProfile && (
          <Link to={`/profiles/me/summary/skills`} className={css.linkWrap}>
            <EditIcon width={12} height={12} className={css.actionIcon} />
          </Link>
        )}
      </div>
    </div>
  );

  return (
    <PageSection
      className={classNames(css.pageSectionWrapper, className)}
      title={t("show.parts.skills")}
      action={actions}
      actionWrapStyle={{ display: "flex", flex: 1, alignItems: "center" }}
      contentStyle={{ overflow: "auto" }}
    >
      {isListVisible || !skillsExplorerAvailable ? (
        <SkillsPowerList
          profile={profile}
          searchTerm={searchTerm}
          skills={skills}
          clearSearch={handleClear}
        />
      ) : (
        <SkillsExplorer profile={profile} skills={skills} selectedChart={selectedChart} />
      )}
    </PageSection>
  );
};

SkillsChartSection.propTypes = {
  profile: PropTypes.shape({
    id: PropTypes.number.isRequired,
    skills: PropTypes.array.isRequired,
    preferences: PropTypes.object.isRequired,
    skill_categories: PropTypes.array
  }).isRequired,
  className: PropTypes.string
};

export default SkillsChartSection;
